import type { RunSourceType } from '../model.js';
import { ENGINE_SHORT_NAMES } from './engine-names.js';

/**
 * Which engines can be chosen, and what choosing one drags in with it.
 *
 * Ported from the legacy frontend's `engineRules.js`, which mirrors the Python source
 * of truth (`app/analysis/shared/domain/engines.py`). The rules are **not** invented
 * here and must not be edited here: this is a copy of somebody else's decision, and
 * the server applies it again on every launch. What this file buys is a dialog that
 * says «coherence needs gestures» before the request goes out, instead of a 422
 * afterwards.
 *
 * Kept in snake_case where the catalogue is snake_case — `implied_by`, `cultural_fit`
 * — because those are the engine's own names as every side of the system spells them.
 * Renaming an identifier is not translation, it is a second name for one thing.
 */

export const ENGINE_NAMES = [
  'gestures',
  'coherence',
  'cultural_fit',
  'client_questions',
  'pre_competency',
  'toolkit',
  'cv',
] as const;

export type EngineName = (typeof ENGINE_NAMES)[number];

export interface EngineCatalogueEntry {
  name: string;
  label: string;
  description: string;
  /** Engines this one cannot run without. Closed transitively. */
  requires: string[];
  implied_by: string[];
  /** Engines this one runs *worse* without. A warning, never a blocker. */
  degraded_without: string[];
  sources: string[];
}

/**
 * A copy of what `GET /videos/engines` serves.
 *
 * The fallback for when that endpoint cannot be reached: the dialog still renders the
 * agreed rules rather than an empty list, and the server validates the launch anyway.
 * Descriptions are in Spanish because they are read by a person; names are not,
 * because they are read by a machine. The labels come from `engine-names.ts`, the one
 * place every screen reads an engine's name from (product owner, 2026-10-14).
 */
export const DEFAULT_ENGINE_CATALOGUE: readonly EngineCatalogueEntry[] = Object.freeze([
  {
    name: 'gestures',
    label: ENGINE_SHORT_NAMES.gestures,
    description: 'Detección de gestos faciales y corporales sobre el vídeo.',
    requires: [],
    implied_by: ['coherence'],
    degraded_without: [],
    sources: ['interview', 'manual_analysis'],
  },
  {
    name: 'coherence',
    label: ENGINE_SHORT_NAMES.coherence,
    description: 'Transcripción, segmentación de ideas y contradicción con los gestos.',
    requires: ['gestures'],
    implied_by: [],
    degraded_without: [],
    sources: ['interview', 'manual_analysis'],
  },
  {
    name: 'cultural_fit',
    label: ENGINE_SHORT_NAMES.cultural_fit,
    description: 'Inferencia de fit cultural sobre la conversación de la entrevista.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'client_questions',
    label: ENGINE_SHORT_NAMES.client_questions,
    description: 'Respuestas a las preguntas del cliente extraídas de la conversación.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'pre_competency',
    label: ENGINE_SHORT_NAMES.pre_competency,
    description: 'Evaluación pre competencial sobre la conversación de la entrevista.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['interview'],
  },
  {
    name: 'toolkit',
    label: ENGINE_SHORT_NAMES.toolkit,
    description: 'Toolkit Triple A. Se ejecuta degradado sin el motor Pre Competencial.',
    requires: [],
    implied_by: [],
    degraded_without: ['pre_competency'],
    sources: ['interview'],
  },
  {
    name: 'cv',
    label: ENGINE_SHORT_NAMES.cv,
    description: 'Análisis de CV.',
    requires: [],
    implied_by: [],
    degraded_without: [],
    sources: ['cv'],
  },
]);

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Fills in every array field and drops entries with no name.
 *
 * The catalogue arrives from an endpoint that may omit an empty array, and code that
 * has to check whether `requires` exists before iterating it is code that will forget
 * to once.
 */
export function normalizeCatalogue(
  catalogue: readonly Partial<EngineCatalogueEntry>[] | null | undefined,
): EngineCatalogueEntry[] {
  return asArray(catalogue as Partial<EngineCatalogueEntry>[])
    .filter((entry): entry is Partial<EngineCatalogueEntry> => Boolean(entry?.name?.trim()))
    .map((entry) => ({
      name: entry.name as string,
      label: entry.label || (entry.name as string),
      description: entry.description ?? '',
      requires: asArray(entry.requires),
      implied_by: asArray(entry.implied_by),
      degraded_without: asArray(entry.degraded_without),
      sources: asArray(entry.sources),
    }));
}

/** The engines a given kind of source can be analysed with, in catalogue order. */
export function selectableEngines(
  catalogue: readonly Partial<EngineCatalogueEntry>[],
  sourceType: RunSourceType | string,
): EngineCatalogueEntry[] {
  return normalizeCatalogue(catalogue).filter((entry) => entry.sources.includes(sourceType));
}

/**
 * What is selected before anybody touches anything: everything selectable.
 *
 * Matches the server's own rule — absent run options means every selectable engine —
 * so the dialog's initial state and a launch that never opened it agree.
 */
export function defaultSelection(
  catalogue: readonly Partial<EngineCatalogueEntry>[],
  sourceType: RunSourceType | string,
): string[] {
  return selectableEngines(catalogue, sourceType).map((entry) => entry.name);
}

export function engineLabel(
  catalogue: readonly Partial<EngineCatalogueEntry>[],
  name: string,
): string {
  return normalizeCatalogue(catalogue).find((entry) => entry.name === name)?.label ?? String(name);
}

export type SelectionWarningCode =
  'engine_unknown' | 'engine_not_selectable' | 'required_by' | 'degraded_without';

export interface SelectionWarning {
  engine: string;
  code: SelectionWarningCode;
  related: string[];
  message: string;
}

export interface Selection {
  /** What will actually run, in catalogue order. */
  engines: string[];
  /** What was asked for, minus anything unusable. */
  requested: string[];
  /** Pulled in by a dependency rather than chosen. The dialog locks these on. */
  autoAdded: string[];
  warnings: SelectionWarning[];
  valid: boolean;
  error: SelectionWarningCode | 'engines_empty' | null;
}

/**
 * Applies the dependency rules to what somebody picked.
 *
 * Three different outcomes, and the difference between them is the whole value of
 * doing this before the request:
 *
 * - **`requires` closes the selection.** Choosing coherence adds gestures, and the
 *   dialog says «lo pide Coherencia» rather than silently analysing more than asked.
 * - **`degraded_without` warns and nothing else.** Toolkit without precompetencias
 *   still runs; it just runs worse, and refusing the launch would be wrong.
 * - **An unknown or unusable engine invalidates the whole thing.** Dropping it
 *   quietly would launch an analysis different from the one somebody chose.
 *
 * An empty effective selection is invalid too: a launch with no engines is a run that
 * does nothing, and the queue would carry it for minutes before saying so.
 */
export function applySelection(
  catalogue: readonly Partial<EngineCatalogueEntry>[],
  sourceType: RunSourceType | string,
  selected: readonly string[] | null | undefined,
): Selection {
  const selectable = selectableEngines(catalogue, sourceType);
  const selectableByName = new Map(selectable.map((entry) => [entry.name, entry]));
  const known = new Set(normalizeCatalogue(catalogue).map((entry) => entry.name));
  const warnings: SelectionWarning[] = [];
  const requested: string[] = [];

  for (const name of asArray(selected as string[])) {
    if (typeof name !== 'string' || requested.includes(name)) continue;

    if (!known.has(name)) {
      warnings.push({
        engine: name,
        code: 'engine_unknown',
        related: [],
        message: `Motor desconocido «${name}»`,
      });
      continue;
    }

    if (!selectableByName.has(name)) {
      warnings.push({
        engine: name,
        code: 'engine_not_selectable',
        related: [],
        message: `${engineLabel(catalogue, name)} no está disponible para este origen`,
      });
      continue;
    }

    requested.push(name);
  }

  // Transitive closure over `requires`, remembering which engine pulled each
  // dependency in — without that, the dialog can lock a switch but not say why.
  const effective = new Set(requested);
  const addedBy = new Map<string, string>();
  const queue = [...requested];

  while (queue.length > 0) {
    const name = queue.shift() as string;
    const entry = selectableByName.get(name);
    if (!entry) continue;

    for (const dependency of entry.requires) {
      if (!selectableByName.has(dependency) || effective.has(dependency)) continue;
      effective.add(dependency);
      addedBy.set(dependency, name);
      queue.push(dependency);
    }
  }

  const engines = selectable.map((entry) => entry.name).filter((name) => effective.has(name));
  const autoAdded = engines.filter((name) => addedBy.has(name));

  for (const name of autoAdded) {
    const by = addedBy.get(name) as string;
    warnings.push({
      engine: name,
      code: 'required_by',
      related: [by],
      message: `lo pide ${engineLabel(catalogue, by)}`,
    });
  }

  for (const name of engines) {
    const entry = selectableByName.get(name) as EngineCatalogueEntry;
    const missing = entry.degraded_without.filter((dependency) => !effective.has(dependency));

    if (missing.length > 0) {
      warnings.push({
        engine: name,
        code: 'degraded_without',
        related: missing,
        message: `se ejecutará degradado sin ${missing
          .map((dependency) => engineLabel(catalogue, dependency))
          .join(', ')}`,
      });
    }
  }

  const blocking = warnings.find(
    (warning) => warning.code === 'engine_unknown' || warning.code === 'engine_not_selectable',
  );

  const error = engines.length === 0 ? 'engines_empty' : (blocking?.code ?? null);

  return { engines, requested, autoAdded, warnings, valid: error === null, error };
}

/**
 * Which switches must stay on because something else needs them.
 *
 * Maps the engine to the one that requires it, so the dialog can both lock the switch
 * and explain the lock. A lock with no reason is a UI people fight with.
 */
export function lockedEngines(
  catalogue: readonly Partial<EngineCatalogueEntry>[],
  sourceType: RunSourceType | string,
  selected: readonly string[],
): Record<string, string> {
  const locked: Record<string, string> = {};

  for (const warning of applySelection(catalogue, sourceType, selected).warnings) {
    if (warning.code === 'required_by') locked[warning.engine] = warning.related[0] as string;
  }

  return locked;
}

export interface RunOptionsDraft {
  engines: string[];
  configVersionId?: string | null;
  batch?: string | null;
  note?: string | null;
  promoteOnSuccess?: boolean | null;
}

function cleanText(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The launch body, with only the fields that carry a value.
 *
 * `engines` is always there; everything else is left out rather than sent empty. An
 * empty `note` and no note are the same intention, and sending `''` writes a note
 * that says nothing onto the run's record forever.
 *
 * `promoteOnSuccess` is the exception: `false` is a decision and must survive, which
 * is why it is checked for being a boolean and not for being truthy.
 */
export function buildRunOptions(draft: RunOptionsDraft): Record<string, unknown> {
  const body: Record<string, unknown> = {
    engines: asArray(draft.engines).filter((name) => typeof name === 'string'),
  };

  const configVersionId = cleanText(draft.configVersionId);
  if (configVersionId) body.configVersionId = configVersionId;

  const batch = cleanText(draft.batch);
  if (batch) body.batch = batch;

  const note = cleanText(draft.note);
  if (note) body.note = note;

  if (typeof draft.promoteOnSuccess === 'boolean') body.promoteOnSuccess = draft.promoteOnSuccess;

  return body;
}

export interface LaunchSummary {
  engines: readonly string[];
  versions?: Record<string, string> | null;
  configName?: string | null;
  configVersion?: number | string | null;
  targetCount?: number;
  /** Singular; the plural is made by adding an s, which is enough for «vídeo» and «grupo». */
  targetNoun?: string;
}

const SHORT_NAMES: Readonly<Record<string, string>> = ENGINE_SHORT_NAMES;

/**
 * The one line at the bottom of the launch dialog.
 *
 * `Gestos 2.0.3 + Veracidad 1.4.2 · Config Base v7 · 3 vídeos`. It is the last thing
 * anybody reads before starting something that takes minutes per video, so it names
 * the engine versions rather than just the engines: the same launch with different
 * versions is a different experiment. Engines by their short label; an unnamed one by its id.
 */
export function formatLaunchSummary({
  engines,
  versions = null,
  configName = null,
  configVersion = null,
  targetCount = 0,
  targetNoun = 'vídeo',
}: LaunchSummary): string {
  const parts: string[] = [];

  const enginePart = asArray(engines as string[])
    .map((name) => `${SHORT_NAMES[name] ?? name}${versions?.[name] ? ` ${versions[name]}` : ''}`)
    .join(' + ');

  if (enginePart) parts.push(enginePart);

  if (configName) {
    const suffix =
      configVersion !== null && configVersion !== undefined && configVersion !== ''
        ? ` v${configVersion}`
        : '';
    parts.push(`Config ${configName}${suffix}`);
  }

  if (targetCount > 0) {
    parts.push(`${targetCount} ${targetNoun}${targetCount === 1 ? '' : 's'}`);
  }

  return parts.join(' · ');
}
